/**
 * 下注档位（sizing preset）计算与筹码取整。见 docs/ARCHITECTURE.md §4.6 / §9.5。
 *
 * 本文件刻意不 import betting.ts：依赖方向是 betting.ts → sizing.ts，反向引用会成环。
 * 所有入参由调用方（legalActions / AI 下注尺度）算好后传入。
 *
 * 红线 #17：所有产出都是整数筹码；红线 #18：`to` 恒为本轮累计投入总额（raise-to）。
 */
import type { Chips } from '../ids.js'
import type { SizingKind, SizingPreset } from './types.js'

/** 档位的池比例。顺序即 UI 顺序，也是去重时「先出现者胜出」的优先级。 */
export const SIZING_FRACTIONS: readonly { readonly kind: SizingKind; readonly fraction: number }[] =
  [
    { kind: 'third', fraction: 1 / 3 },
    { kind: 'half', fraction: 1 / 2 },
    { kind: 'twoThirds', fraction: 2 / 3 },
    { kind: 'pot', fraction: 1 },
  ]

export interface SizingContext {
  /** 本人本轮已投入 */
  readonly committedThisRound: Chips
  /** 本人待跟的增量；bet 场景为 0 */
  readonly toCall: Chips
  /** 已收池 + 本街所有人已投入（不含本人待跟） */
  readonly potForSizing: Chips
  /** 已被 maxRaiseTo 钳位的最小加注额 */
  readonly minRaiseTo: Chips
  /** = committedThisRound + stack */
  readonly maxRaiseTo: Chips
  readonly bigBlind: Chips
}

/**
 * 取整步长。必须用 **bb 的倍数**分档，不能用绝对筹码数：
 * 房间若 bb=1000（纯计分道具），按 200/1000/5000 分档会把任何小于 500 的下注取整成 0。
 *
 * bb/4、bb/2 在小盲注下不是整数（bb=2 → 0.5），故整体下取整并兜底到 1（红线 #17）。
 */
export function chipStep(to: number, bigBlind: Chips): Chips {
  const bb = Math.max(1, Math.floor(bigBlind))
  const raw = to < 4 * bb ? bb / 4 : to < 20 * bb ? bb / 2 : to < 100 * bb ? bb : bb * 2
  return Math.max(1, Math.floor(raw))
}

/** 只取整、不钳位。AI 的下注尺度需要先看未钳位值再决定是否退化为 call（§9.5）。 */
export function snapToChipStep(to: number, bigBlind: Chips): Chips {
  const step = chipStep(to, bigBlind)
  return Math.round(to / step) * step
}

/**
 * 取整 + 钳到 [minLegal, maxLegal]。
 * minLegal > maxLegal（短码，理论上不可达）时以 maxLegal 胜出，保证结果永远 <= 玩家筹码。
 */
export function snapAmount(
  to: number,
  bigBlind: Chips,
  minLegal: Chips,
  maxLegal: Chips,
): Chips {
  return Math.min(Math.max(snapToChipStep(to, bigBlind), minLegal), maxLegal)
}

/**
 * 档位计算与去重（§4.6）：
 *
 *   potAfterCall = potForSizing + toCall
 *   rawSizing(f) = committedThisRound + toCall + f * potAfterCall
 *   raw < minRaiseTo  → 丢弃（UI 置灰，绝不静默上调）
 *   raw >= maxRaiseTo → 归一为 allIn
 *   按 to 去重，先出现者胜出
 *
 * 去重是必需的而非优化：短码时五个档位会全部钳到 maxRaiseTo，不去重 UI 会渲染 5 个
 * 一模一样的按钮。
 *
 * 调用前提：canRaise === true。不可加注时 legalActions 直接给空数组，
 * 否则 UI 会在「只能 call/fold」的节点上渲染出一个 all-in 按钮。
 */
export function sizingPresets(ctx: SizingContext): SizingPreset[] {
  const { committedThisRound, toCall, potForSizing, minRaiseTo, maxRaiseTo, bigBlind } = ctx
  const potAfterCall = potForSizing + toCall
  // bet 时 toCall=0，于是 base 退化为 committedThisRound，与规格的两条公式统一
  const base = committedThisRound + toCall

  const out: SizingPreset[] = []
  const seen = new Set<Chips>()
  const push = (kind: SizingKind, to: Chips): void => {
    if (seen.has(to)) return
    seen.add(to)
    out.push({ kind, to })
  }

  for (const { kind, fraction } of SIZING_FRACTIONS) {
    const raw = base + fraction * potAfterCall
    if (raw < minRaiseTo) continue
    if (raw >= maxRaiseTo) {
      push('allIn', maxRaiseTo)
      continue
    }
    const to = snapAmount(raw, bigBlind, minRaiseTo, maxRaiseTo)
    // 取整后落到上限的档位同样标成 allIn：否则它会把后面的 allIn 条目去重掉，
    // UI 就彻底没有 all-in 按钮了
    push(to >= maxRaiseTo ? 'allIn' : kind, to)
  }
  push('allIn', maxRaiseTo)

  // 防御性兜底：maxRaiseTo >= minRaiseTo 恒成立（minRaiseTo 已被 maxRaiseTo 钳位），
  // 故上面的 allIn 永不被丢弃，此分支实际不可达
  return out.length > 0 ? out : [{ kind: 'allIn', to: maxRaiseTo }]
}
